// Baselayer Background Checks — UCC filing detail at
// /background-checks/ucc-filings/:id. Draft editor + edit history + the
// status-driven workflow actions: Draft → Submit for approval (UccDraft);
// PendingApproval → Approve / Reject (UccApprove); Approved → record the
// console-entered Baselayer filing id (UccApprove).

import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  DetailLayout,
  DetailSection,
  Input,
  Label,
  PageHeader,
  Skeleton,
  StatusBadge,
  Timestamp,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { ApiError } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import {
  useApproveBgcUccFiling,
  useBgcUccFiling,
  useBgcUccFilingEdits,
  useRecordBgcUccManualFilingId,
  useRejectBgcUccFiling,
  useSubmitBgcUccFilingForApproval,
  useUpdateBgcUccFiling,
} from "@/hooks/backgroundChecks/useBgcUccFiling";
import { UccFilingEditor } from "@/components/backgroundChecks/UccFilingEditor";
import type {
  BgcUccFilingDraftRequest,
  BgcUccFilingDto,
} from "@/api/backgroundChecks/bgcUccFilingApi";
import {
  UCC_FILING_TYPE_LABELS,
  uccStatusTone,
  UCC_FILING_STATUS_LABELS,
} from "@/components/backgroundChecks/checkFormat";

function toDraft(f: BgcUccFilingDto): BgcUccFilingDraftRequest {
  return {
    filingType: f.filingType,
    backgroundCheckId: f.backgroundCheckId,
    dealId: f.dealId,
    businessId: f.businessId,
    loanId: f.loanId,
    isLongTerm: f.isLongTerm,
    parentServicingFilingId: f.parentServicingFilingId,
    filingState: f.filingState,
    debtorLegalName: f.debtorLegalName,
    debtorType: f.debtorType,
    debtorMailingLine1: f.debtorMailingLine1,
    debtorCity: f.debtorCity,
    debtorState: f.debtorState,
    debtorZip: f.debtorZip,
    debtorJurisdictionOfOrg: f.debtorJurisdictionOfOrg,
    securedPartyLegalName: f.securedPartyLegalName,
    securedPartyType: f.securedPartyType,
    securedPartyMailingLine1: f.securedPartyMailingLine1,
    securedPartyCity: f.securedPartyCity,
    securedPartyState: f.securedPartyState,
    securedPartyZip: f.securedPartyZip,
    collateralDescriptionType: f.collateralDescriptionType,
    collateralDescriptionText: f.collateralDescriptionText,
  };
}

export default function BgcUccFilingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const { toast } = useToast();

  const filingQuery = useBgcUccFiling(id);
  const editsQuery = useBgcUccFilingEdits(id);

  const update = useUpdateBgcUccFiling(id ?? "");
  const submit = useSubmitBgcUccFilingForApproval(id ?? "");
  const approve = useApproveBgcUccFiling(id ?? "");
  const reject = useRejectBgcUccFiling(id ?? "");
  const recordManual = useRecordBgcUccManualFilingId(id ?? "");

  const [draft, setDraft] = useState<BgcUccFilingDraftRequest | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [manualFilingId, setManualFilingId] = useState("");

  const filing = filingQuery.data;
  useEffect(() => {
    if (filing) setDraft(toDraft(filing));
  }, [filing]);

  const canDraft = auth.hasPermission("BackgroundCheck.UccDraft");
  const canApprove = auth.hasPermission("BackgroundCheck.UccApprove");

  const runAction = async (
    fn: () => Promise<unknown>,
    successMessage: string,
  ) => {
    try {
      await fn();
      toast({ message: successMessage, tone: "success" });
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.errors[0] ?? err.message
          : err instanceof Error
            ? err.message
            : "Action failed.";
      toast({ message, tone: "error" });
    }
  };

  if (filingQuery.isLoading || !draft) {
    return (
      <div className="flex flex-col gap-space-4">
        <Skeleton className="h-12 w-1/2" />
        <Skeleton className="h-72 w-full" />
      </div>
    );
  }

  if (!filing) {
    return (
      <DetailSection title="UCC filing not found">
        <p className="text-small text-gray-mid m-0">
          The UCC filing you requested was not found or you don&apos;t have
          permission to view it.
        </p>
        <button
          type="button"
          className="mt-space-3 text-gold-shadow hover:underline text-small"
          onClick={() => navigate("/background-checks")}
        >
          Back to Background Checks
        </button>
      </DetailSection>
    );
  }

  const isDraft = filing.status === "Draft";
  const isPendingApproval = filing.status === "PendingApproval";
  const isApproved = filing.status === "Approved";
  const editable = isDraft && canDraft;

  return (
    <DetailLayout
      header={
        <PageHeader
          breadcrumbs={[
            { label: "Background Checks", to: "/background-checks" },
            { label: UCC_FILING_TYPE_LABELS[filing.filingType] },
          ]}
          title={filing.debtorLegalName || "UCC filing"}
          eyebrow={UCC_FILING_TYPE_LABELS[filing.filingType]}
          actions={
            <StatusBadge tone={uccStatusTone(filing.status)}>
              {UCC_FILING_STATUS_LABELS[filing.status]}
            </StatusBadge>
          }
        />
      }
      aside={
        <DetailSection title="Actions">
          {isDraft && (
            <div className="flex flex-col gap-space-3">
              {editable ? (
                <Button
                  type="button"
                  variant="secondary"
                  loading={update.isPending}
                  disabled={update.isPending}
                  onClick={() =>
                    runAction(
                      () => update.mutateAsync(draft),
                      "Draft saved.",
                    )
                  }
                  data-testid="ucc-save-draft"
                >
                  Save draft
                </Button>
              ) : null}
              {canDraft ? (
                <Button
                  type="button"
                  loading={submit.isPending}
                  disabled={submit.isPending}
                  onClick={() =>
                    runAction(
                      () => submit.mutateAsync(),
                      "Submitted for approval.",
                    )
                  }
                  data-testid="ucc-submit"
                >
                  Submit for approval
                </Button>
              ) : (
                <p className="text-small text-gray-mid m-0">
                  You don&apos;t have permission to submit this draft.
                </p>
              )}
            </div>
          )}

          {isPendingApproval &&
            (canApprove ? (
              <div className="flex flex-col gap-space-3">
                <Button
                  type="button"
                  loading={approve.isPending}
                  disabled={approve.isPending}
                  onClick={() =>
                    runAction(() => approve.mutateAsync(), "Filing approved.")
                  }
                  data-testid="ucc-approve"
                >
                  Approve
                </Button>
                <div className="flex flex-col gap-space-1">
                  <Label htmlFor="ucc-reject-reason">Rejection reason</Label>
                  <Input
                    id="ucc-reject-reason"
                    value={rejectReason}
                    onChange={(e) => setRejectReason(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  variant="destructive"
                  loading={reject.isPending}
                  disabled={reject.isPending || !rejectReason.trim()}
                  onClick={() =>
                    runAction(
                      () => reject.mutateAsync(rejectReason.trim()),
                      "Filing rejected.",
                    )
                  }
                  data-testid="ucc-reject"
                >
                  Reject
                </Button>
              </div>
            ) : (
              <p className="text-small text-gray-mid m-0">
                Awaiting approval by an authorized reviewer.
              </p>
            ))}

          {isApproved &&
            (canApprove ? (
              <div className="flex flex-col gap-space-3">
                <p className="text-small text-gray-mid m-0">
                  File this UCC in the Baselayer console, then record the
                  returned filing ID below.
                </p>
                <div className="flex flex-col gap-space-1">
                  <Label htmlFor="ucc-manual-filing-id">
                    Baselayer filing ID
                  </Label>
                  <Input
                    id="ucc-manual-filing-id"
                    value={manualFilingId}
                    onChange={(e) => setManualFilingId(e.target.value)}
                  />
                </div>
                <Button
                  type="button"
                  loading={recordManual.isPending}
                  disabled={recordManual.isPending || !manualFilingId.trim()}
                  onClick={() =>
                    runAction(
                      () =>
                        recordManual.mutateAsync(manualFilingId.trim()),
                      "Filing ID recorded.",
                    )
                  }
                  data-testid="ucc-record-filing-id"
                >
                  Record filing ID
                </Button>
              </div>
            ) : (
              <p className="text-small text-gray-mid m-0">
                Approved — awaiting filing in the Baselayer console.
              </p>
            ))}

          {!isDraft && !isPendingApproval && !isApproved && (
            <p className="text-small text-gray-mid m-0">
              No further actions are available in this state.
            </p>
          )}

          {filing.rejectionReason && (
            <p className="text-small text-status-error mt-space-3">
              Rejected: {filing.rejectionReason}
            </p>
          )}
          {filing.baselayerFilingId && (
            <p className="text-small text-gray-mid mt-space-3">
              Baselayer filing ID: {filing.baselayerFilingId}
            </p>
          )}
        </DetailSection>
      }
    >
      <DetailSection title="Filing draft">
        <UccFilingEditor value={draft} onChange={setDraft} disabled={!editable} />
      </DetailSection>

      <DetailSection title="Edit history">
        {editsQuery.isLoading ? (
          <p className="text-small text-gray-mid m-0">Loading history…</p>
        ) : (editsQuery.data?.length ?? 0) === 0 ? (
          <p className="text-small text-gray-mid m-0">No edits recorded yet.</p>
        ) : (
          <ul className="flex flex-col gap-space-2 list-none m-0 p-0">
            {editsQuery.data!.map((e) => (
              <li
                key={e.id}
                className="flex flex-wrap items-baseline gap-space-2 border-b border-border-light pb-space-2 last:border-0"
              >
                <span className="text-small font-semibold text-onyx">
                  {e.action}
                </span>
                <Timestamp value={e.timestamp} />
                {e.notes && (
                  <span className="text-caption text-gray-mid">{e.notes}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </DetailSection>
    </DetailLayout>
  );
}
